import "./index.scss";

import { type ComponentProps, type ReactNode, useState } from "react";
import classnames from "classnames";
import { useLocation, useParams } from "react-router-dom";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { useFeatureFlag } from "common/feature_gating";
import { NetworkStatus, useQuery } from "util/graphql";
import Modal from "common/modal";
import Tabs from "common/core/tabs";
import Tab from "common/core/tabs/tab";
import DocumentTabName from "common/details/bundle/document_tab_name";
import NotificationBar from "common/core/notification_bar";
import TransactionResendModal from "common/transactions/actions/resend";
import FooterActions from "common/core/footer_actions";
import { useAnalyticsContext } from "common/core/manage_analytics_context";
import { isNotaryNST } from "common/notary/capacity";
import { getParentPathByOffset, newPathWithPreservedSearchParams } from "util/location";
import CompletionRequirementsText from "util/completion_requirements/completion_requirements_text";
import { dateComparator } from "util/date";
import { getLastStatus } from "util/history";
import { TRANSACTION_TYPE_BUSINESS } from "constants/analytics";
import { AUDIT_TRAIL_STATES, EMAIL_OPTION_PLACEHOLDERS } from "constants/transaction";
import { useActiveOrganization } from "common/account/active_organization";
import LoadingIndicator from "common/core/loading_indicator";
import { sandboxEnabled } from "config/environment/sandbox";
import {
  Feature,
  MeetingEndedState,
  MortgageTransactionType,
  OrganizationMembershipRole,
  OrgTransactionStates,
} from "graphql_globals";
import { includesOrgAdminRole } from "util/organization";
import { SELF_SERVE_FORCE_COMPLETE } from "constants/feature_gates";

import DefaultTransactionDetailsModalQuery, {
  type DefaultTransactionDetailsModal_node_OrganizationTransaction as OrganizationTransaction,
  type DefaultTransactionDetailsModal_node_OrganizationTransaction_bundle_meetings as BundleMeetings,
  type DefaultTransactionDetailsModal_viewer as Viewer,
} from "./index_query.graphql";
import TransactionDetailsActions from "./transaction_details_actions";
import { usePermissions } from "../../core/current_user_role";

type ChildRenderProps = {
  organization: {
    id: string;
    titleAgencyAccess: boolean | null;
  };
  transaction: OrganizationTransaction;
  bundle: NonNullable<OrganizationTransaction["bundle"]>;
  meeting: null | BundleMeetings["edges"][number]["node"];
  finalizedDocumentOnly: boolean;
  viewer: Viewer;
  refetch: () => Promise<unknown>;
};
type Props = {
  children: (renderProps: ChildRenderProps) => ReactNode;
  withNotes?: boolean;
  withActivityLog?: boolean;
  className?: string;
  /** null means don't render anything */
  loadedMapFn?: (props: LoadedProps) => Partial<LoadedProps> | null;
};
type LoadedProps = Omit<Props, "loadedMapFn"> & {
  node: null | ChildRenderProps["transaction"];
  viewer: ChildRenderProps["viewer"];
  organization: ChildRenderProps["organization"];
  refetch: ChildRenderProps["refetch"];
};

const MESSAGES = defineMessages({
  title: {
    id: "d6fd35c3-d1ba-45e0-baa7-dc6bb277cd74",
    defaultMessage: "Transaction Details",
  },
  failAlert: {
    id: "2c51f824-f267-4276-811f-5c311a157ce8",
    defaultMessage: "Sorry, something went wrong. Please try again.",
  },
});

function renderSummaryTab(search: string, path: string) {
  return (
    <Tab to={`${path}/summary`} search={search} automationId="summary-tab">
      <FormattedMessage id="a95b5c40-cffe-453d-8bf9-1da194659860" defaultMessage="Summary" />
    </Tab>
  );
}

function renderPointsOfContactTab(
  search: string,
  path: string,
  transaction: OrganizationTransaction,
) {
  return transaction.isMortgage || transaction.contacts.length ? (
    <Tab to={`${path}/contacts`} search={search} automationId="contacts-tab">
      <FormattedMessage id="43b51916-53a7-430b-97d6-d205bc20bb12" defaultMessage="Contacts" />
    </Tab>
  ) : null;
}

function renderSignerTab(search: string, path: string, transaction: OrganizationTransaction) {
  return transaction.isMortgage &&
    transaction.transactionType === MortgageTransactionType.wet_sign ? null : (
    <Tab to={`${path}/user`} search={search} automationId="signer-tab">
      <FormattedMessage id="c2413441-b8e3-4e9d-b331-06afade1f039" defaultMessage="Signer" />
    </Tab>
  );
}

function IdentityDetailsTab(props: {
  search: string;
  path: string;
  transaction: OrganizationTransaction;
  viewer: Viewer;
}) {
  const { hasPermissionFor } = usePermissions();
  const isOrgAdmin = includesOrgAdminRole(props.viewer.user?.roles || []);
  const identityTabEnabled = useFeatureFlag("identity-details-modal");
  const showIdentityDetailsTab =
    (isOrgAdmin && identityTabEnabled) || hasPermissionFor("viewIdentityDetails");

  if (showIdentityDetailsTab) {
    return (
      <Tab to={`${props.path}/identity`} search={props.search} automationId="identity-tab">
        <FormattedMessage id="b33c3761-be3f-482a-8f9c-2ff9039f7194" defaultMessage="Identity" />
      </Tab>
    );
  }
  return null;
}

function renderDocumentTab(
  search: string,
  path: string,
  bundle: ComponentProps<typeof DocumentTabName>["bundle"],
) {
  return (
    <Tab to={`${path}/document`} search={search} automationId="documents-tab">
      <DocumentTabName bundle={bundle} />
    </Tab>
  );
}

function renderHistoryTab(search: string, path: string) {
  return (
    <Tab to={`${path}/history`} search={search} automationId="history-tab">
      <FormattedMessage id="96540c10-3ecb-4e5d-b439-b71619b105b8" defaultMessage="History" />
    </Tab>
  );
}

function renderNotaryNotesTab(search: string, path: string, withNotes: boolean | undefined) {
  return withNotes ? (
    <Tab to={`${path}/notes`} search={search} automationId="notes-tab">
      <FormattedMessage id="f445be29-0b62-462c-8a0d-550848b10e38" defaultMessage="Notes" />
    </Tab>
  ) : null;
}

function renderWitnessTab(search: string, path: string, meetings: BundleMeetings) {
  const someMeetingHasWitness = meetings.edges.some((edge) =>
    edge.node.meetingParticipants.some((p) => p.__typename === "WitnessParticipant"),
  );
  return someMeetingHasWitness ? (
    <Tab to={`${path}/witness`} search={search} automationId="witness-tab">
      <FormattedMessage id="3309221f-7159-47ca-8bfb-69c17314e389" defaultMessage="Witness" />
    </Tab>
  ) : null;
}

function renderTestTab(search: string, path: string) {
  return sandboxEnabled() ? (
    <Tab to={`${path}/test-setup`} search={search} automationId="test-setup-tab">
      <FormattedMessage id="61f70164-1ff8-422f-a6dc-565a246771c3" defaultMessage="Test Setup" />
    </Tab>
  ) : null;
}

function renderActivityLogsTab(search: string, path: string, withActivityLog: boolean | undefined) {
  return withActivityLog ? (
    <Tab to={`${path}/activity`} search={search} automationId="activity-tab">
      <FormattedMessage id="61f70164-1ff8-422f-a6dc-565a446771c3" defaultMessage="Activity Log" />
    </Tab>
  ) : null;
}

function canResendTransaction(
  transaction: OrganizationTransaction,
  activeOrganizationId: string | null,
): boolean {
  const { transactionType, state, publicOrganization } = transaction;
  const validState = [
    OrgTransactionStates.SENT,
    OrgTransactionStates.RECEIVED,
    OrgTransactionStates.ATTEMPTED,
  ].includes(state!);

  return (
    validState &&
    publicOrganization.id === activeOrganizationId &&
    !publicOrganization.suppressSignerEmails &&
    transactionType !== MortgageTransactionType.wet_sign
  );
}

function renderMeetingTabs(
  search: string,
  path: string,
  transaction: OrganizationTransaction,
  showFullRecord: boolean,
) {
  const { completionRequirements, finalizedMeetings } = transaction.bundle!;
  if (!finalizedMeetings.totalCount) {
    return null;
  }
  return (
    <>
      <Tab to={`${path}/notary`} search={search} automationId="notary-tab">
        <CompletionRequirementsText
          stringId="notary"
          completionRequirements={completionRequirements}
        />
      </Tab>
      {showFullRecord && (
        <Tab to={`${path}/video`} search={search} automationId="video-tab">
          <FormattedMessage id="82cd34bf-b16e-4f8e-b2ce-70795b1255ee" defaultMessage="Video" />
        </Tab>
      )}
    </>
  );
}

function getIncompleteMeeting(meetings: BundleMeetings) {
  const incompleteMeetings = meetings.edges.filter(
    (edge) => edge.node.endedState === MeetingEndedState.NOT_COMPLETED,
  );
  return incompleteMeetings.length
    ? incompleteMeetings.sort((a, b) =>
        dateComparator(b.node.timeFrame!.startedAt, a.node.timeFrame!.startedAt),
      )[0].node
    : null;
}

function useAnalytics(transaction: LoadedProps["node"]) {
  useAnalyticsContext(() => {
    if (!transaction) {
      return;
    }
    const { payer, transactionCreatorName, publicOrganization, guestEnabled, bundle } = transaction;

    return {
      transaction_type: TRANSACTION_TYPE_BUSINESS,
      transaction_creator_first_name: transactionCreatorName.firstName,
      transaction_creator_last_name: transactionCreatorName.lastName,
      document_count: bundle!.documents.totalCount,
      signer_count: bundle!.signers!.length,
      org_id: publicOrganization.id,
      payer,
      guest_enabled: guestEnabled,
    };
  });
}

function TransactionDetailsLoaded(props: LoadedProps) {
  const { node: transaction, viewer, refetch } = props;

  const { pathname, search } = useLocation();
  const intl = useIntl();
  useAnalytics(transaction);
  const activeOrganizationId = props.organization.id;
  const [resendTransactionOpen, setResendTransactionOpen] = useState(false);
  const [showFailedBanner, setShowFailedBanner] = useState(false);
  const forceCompleteEnabled = useFeatureFlag(SELF_SERVE_FORCE_COMPLETE);
  const { hasPermissionFor } = usePermissions();

  if (!transaction) {
    return null;
  }

  const { state, canRecall, archived } = transaction;
  const bundle = transaction.bundle!;
  const { auditTrails } = bundle;
  const { notaryProfile, organization: userOrganization, organizationMembership } = viewer.user!;
  const path = getParentPathByOffset(pathname, 1);
  const closeRoute = getParentPathByOffset(pathname, 2);
  const showFullRecord = transaction.publicOrganization.featureList.includes(
    Feature.TRANSACTION_RECORD_FULL_ACCESS,
  );
  const organizationRole = organizationMembership?.role;
  // Everyone who is not an NST notary or an NST from any state but WA
  const showDocumentTab = Boolean(
    !notaryProfile || !isNotaryNST(notaryProfile) || notaryProfile.usState.abbreviation !== "WA",
  );
  const showArchiveButton =
    !archived &&
    showFullRecord &&
    [OrgTransactionStates.COMPLETED, OrgTransactionStates.COMPLETED_WITH_REJECTIONS].includes(
      state!,
    );
  const showUnarchiveButton =
    archived &&
    showFullRecord &&
    [OrgTransactionStates.COMPLETED, OrgTransactionStates.COMPLETED_WITH_REJECTIONS].includes(
      state!,
    );
  const showCancelButton =
    state !== OrgTransactionStates.COMPLETED &&
    transaction.publicOrganization.id === activeOrganizationId &&
    getLastStatus(auditTrails, state) !== AUDIT_TRAIL_STATES.MeetingInProgress;
  const showRecallButton =
    canRecall && transaction.transactionType !== MortgageTransactionType.wet_sign;
  const showDuplicateButton =
    organizationRole === OrganizationMembershipRole.NOTARIZE_CLOSING_OPS &&
    userOrganization?.featureList.includes(Feature.DUPLICATE_TRANSACTIONS);
  const showForceCompleteButton =
    hasPermissionFor("selfServeForceComplete") &&
    forceCompleteEnabled &&
    ![OrgTransactionStates.COMPLETED, OrgTransactionStates.COMPLETED_WITH_REJECTIONS].includes(
      state!,
    );
  const showFooterActions =
    showRecallButton ||
    showCancelButton ||
    showArchiveButton ||
    showUnarchiveButton ||
    showDuplicateButton ||
    showForceCompleteButton;

  return (
    <Modal
      title={intl.formatMessage(MESSAGES.title)}
      className={classnames("TransactionDetails", props.className)}
      closeRoute={newPathWithPreservedSearchParams(closeRoute)}
    >
      <Tabs modalView>
        {renderSummaryTab(search, path)}
        {renderPointsOfContactTab(search, path, transaction)}
        {renderSignerTab(search, path, transaction)}
        <IdentityDetailsTab search={search} path={path} transaction={transaction} viewer={viewer} />
        {renderWitnessTab(search, path, bundle.meetings)}
        {renderMeetingTabs(search, path, transaction, showFullRecord)}
        {showDocumentTab && renderDocumentTab(search, path, bundle)}
        {showFullRecord && auditTrails.length > 0 && renderHistoryTab(search, path)}
        {renderNotaryNotesTab(search, path, props.withNotes)}
        {renderActivityLogsTab(search, path, props.withActivityLog)}
        {renderTestTab(search, path)}
      </Tabs>
      {canResendTransaction(transaction, activeOrganizationId) && (
        <NotificationBar displayIcon={false}>
          <span
            data-automation-id="resend-txn-to-signer-link"
            className="SMBResendTransaction--action"
            onClick={() => setResendTransactionOpen(true)}
          >
            <FormattedMessage
              id="236874de-6687-4304-b287-bceeb9705508"
              defaultMessage="Resend Transaction To Signer(s)"
            />
          </span>
        </NotificationBar>
      )}
      {props.children({
        bundle,
        meeting: getIncompleteMeeting(bundle.meetings),
        transaction,
        finalizedDocumentOnly: false,
        viewer,
        organization: props.organization,
        refetch,
      })}
      {showFooterActions && (
        <FooterActions
          errorMessage={showFailedBanner ? intl.formatMessage(MESSAGES.failAlert) : undefined}
          rightButtons={
            <TransactionDetailsActions
              transaction={transaction}
              viewer={viewer}
              showCancelButton={showCancelButton}
              showRecallButton={showRecallButton}
              archiveButton={
                showArchiveButton ? "archive" : showUnarchiveButton ? "unarchive" : undefined
              }
              onArchiveFailure={() => setShowFailedBanner(true)}
              onArchiveSuccess={() => setShowFailedBanner(false)}
              showDuplicateButton={showDuplicateButton}
              showForceCompleteButton={showForceCompleteButton}
            />
          }
        />
      )}

      <TransactionResendModal
        transaction={transaction}
        isOpen={resendTransactionOpen}
        onClose={() => setResendTransactionOpen(false)}
        messagePlaceholder={intl.formatMessage(EMAIL_OPTION_PLACEHOLDERS.MESSAGE_PLACEHOLDER)}
        refetch={refetch}
      />
    </Modal>
  );
}

function TransactionDetails(props: Props) {
  const [activeOrganizationId] = useActiveOrganization();
  const { transactionID } = useParams();
  const { networkStatus, data, refetch } = useQuery(DefaultTransactionDetailsModalQuery, {
    variables: { organizationId: activeOrganizationId!, transactionId: transactionID! },
  });

  if (networkStatus === NetworkStatus.loading) {
    return <LoadingIndicator />;
  }

  const { organization, node, viewer } = data!;
  if (organization?.__typename !== "Organization") {
    throw new Error(`Expected Organization, got ${organization?.__typename}`);
  }
  if (node?.__typename !== "OrganizationTransaction") {
    throw new Error(`Expected OrganizationTransaction, got ${node?.__typename}`);
  }

  const fullProps = { ...props, node, organization, viewer, refetch };
  if (props.loadedMapFn) {
    const mappedProps = props.loadedMapFn(fullProps);
    return mappedProps && <TransactionDetailsLoaded {...fullProps} {...mappedProps} />;
  }
  return <TransactionDetailsLoaded {...fullProps} />;
}

export default TransactionDetails;
